import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { api, type InfrastructureOverview } from "@/lib/api";
import { relativeIso } from "@/lib/format";
import { ProviderBadge } from "@/pages/admin/SandboxProviderSection";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

// ── Types ────────────────────────────────────────────────────────

type Container = InfrastructureOverview["containers"][number];
type PoolContainer = InfrastructureOverview["poolContainers"][number];

// ── Shared cell renderers ────────────────────────────────────────

function OrgLink({ orgId, orgName }: { orgId: string; orgName: string }) {
  return (
    <Link
      to="/superadmin/organizations/$orgId"
      params={{ orgId }}
      target="_blank"
      className="text-sm text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
    >
      {orgName}
    </Link>
  );
}

// Sort by last activity, most-recent first. Containers that have never
// recorded activity sink to the bottom — that's where admins can spot the
// truly idle ones at a glance. Tie-breaker is createdAt desc so newly
// provisioned rows with no activity yet still appear above ancient idles.
function sortContainers(rows: Container[]): Container[] {
  return [...rows].sort((a, b) => {
    const aMs = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
    const bMs = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
    if (aMs !== bMs) return bMs - aMs;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

// ── Column definitions ───────────────────────────────────────────

const containerColumns: ColumnDef<Container>[] = [
  { accessorKey: "name", header: "Name" },
  {
    accessorKey: "organizationName",
    header: "Organization",
    cell: ({ row }) => <OrgLink orgId={row.original.organizationId} orgName={row.original.organizationName} />,
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => {
      const { deploymentStatus, idle, errorMessage } = row.original;
      if (idle) return <StatusBadge status="idle" />;
      return <StatusBadge status={deploymentStatus} errorMessage={errorMessage} />;
    },
  },
  {
    accessorKey: "sandboxProvider",
    header: "Sandbox",
    cell: ({ getValue }) => <ProviderBadge provider={getValue() as string} />,
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{relativeIso(getValue() as string)}</span>
    ),
  },
  {
    accessorKey: "lastActivity",
    header: "Last Activity",
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{relativeIso(getValue() as string | null)}</span>
    ),
  },
];

const poolColumns: ColumnDef<PoolContainer>[] = [
  { accessorKey: "name", header: "Name" },
  {
    accessorKey: "deploymentStatus",
    header: "Status",
    cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{relativeIso(getValue() as string)}</span>
    ),
  },
];

// ── Stat card ────────────────────────────────────────────────────

function StatCard({ title, value, accent }: { title: string; value: string | number; accent?: string }) {
  return (
    <Card className="py-4">
      <CardHeader className="pb-0">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-2xl font-semibold tabular-nums ${accent ?? ""}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

// ── Page component ───────────────────────────────────────────────

export function AdminInfrastructurePage() {
  useDocumentTitle("Admin: Infrastructure");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const openContainer = (id: string) =>
    navigate({ to: "/superadmin/infrastructure/$containerId", params: { containerId: id } });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-infrastructure"],
    queryFn: api.admin.infrastructure,
    refetchInterval: 30_000,
  });

  const reconcile = useMutation({
    mutationFn: api.admin.reconcileStatuses,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin-infrastructure"] });
      const { reconciled, failed } = result;
      if (reconciled.length > 0) {
        toast.success(`Updated ${reconciled.length} container${reconciled.length > 1 ? "s" : ""}`);
      }
      if (failed.length > 0) {
        toast.error(`Failed to check ${failed.length} container${failed.length > 1 ? "s" : ""}: ${failed.map((f) => f.name).join(", ")}`);
      }
      if (reconciled.length === 0 && failed.length === 0) {
        toast.info("All statuses are up to date");
      }
    },
  });

  const togglePool = useMutation({
    mutationFn: api.admin.setWarmPoolEnabled,
    onSuccess: ({ enabled }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-infrastructure"] });
      toast.success(enabled ? "Warm pool enabled" : "Warm pool disabled — new containers will provision from scratch");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update warm pool setting");
    },
  });

  const provisionPool = useMutation({
    mutationFn: api.admin.provisionPoolContainer,
    onSuccess: ({ agentType }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-infrastructure"] });
      toast.success(`Provisioning a ${agentType} pool container — it'll appear below as soon as Railway acknowledges`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to start pool provisioning");
    },
  });

  const syncStatus = useQuery({
    queryKey: ["admin-compute-sync-status"],
    queryFn: api.admin.computeSyncStatus,
    refetchInterval: 60_000,
  });

  // Cron liveness (BOO-84): heartbeats stamped by each scheduled endpoint;
  // a red row means that schedule silently stopped firing.
  const cronHealth = useQuery({
    queryKey: ["admin-cron-health"],
    queryFn: api.admin.cronHealth,
    refetchInterval: 60_000,
  });

  const syncCompute = useMutation({
    mutationFn: api.admin.syncCompute,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["admin-compute-sync-status"] });
      toast.success(
        `Synced ${r.synced} containers (${r.errors} errors, ${r.skipped} skipped) · raw $${r.totalRawCost.toFixed(4)} · billed $${r.totalMarkedUpCost.toFixed(4)}`,
      );
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to sync compute");
    },
  });

  const onVercel = data?.containers.filter((c) => c.sandboxProvider === "vercel").length ?? 0;

  const hasStale =
    data?.poolContainers.some((c) => c.deploymentStatus === "creating" || c.deploymentStatus === "error") ||
    data?.containers.some((c) => c.deploymentStatus === "error" || c.deploymentStatus === "creating" || c.deploymentStatus === "restarting");

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title="Infrastructure" subtitle="Platform health and resource overview." />
        <div className="flex items-center gap-2">
          <div className="text-xs text-muted-foreground">
            Compute sync{" "}
            <span className={syncStatus.data && !syncStatus.data.healthy ? "text-destructive font-medium" : ""}>
              {syncStatus.data?.lastSyncAt
                ? relativeIso(syncStatus.data.lastSyncAt)
                : "never"}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncCompute.mutate()}
            disabled={syncCompute.isPending}
          >
            <RefreshCw className={`size-3.5 mr-1.5 ${syncCompute.isPending ? "animate-spin" : ""}`} />
            Sync compute
          </Button>
          {hasStale && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => reconcile.mutate()}
              disabled={reconcile.isPending}
            >
              <RefreshCw className={`size-3.5 mr-1.5 ${reconcile.isPending ? "animate-spin" : ""}`} />
              Check statuses
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <LoadingIndicator />
      ) : isError || !data ? (
        <ErrorComponent error={error ?? "Failed to load infrastructure data"} />
      ) : (
        <div className="space-y-8">
          {cronHealth.data && (
            <div className="rounded-md border p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Cron health</h3>
                <span
                  className={`text-xs font-medium ${cronHealth.data.ok ? "text-emerald-600" : "text-destructive"}`}
                >
                  {cronHealth.data.ok ? "All schedules alive" : "Schedule stopped"}
                </span>
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-3 lg:grid-cols-5">
                {cronHealth.data.crons.map((cron) => (
                  <div
                    key={cron.name}
                    className={`rounded border px-3 py-2 text-xs ${cron.ok ? "" : "border-destructive/60 bg-destructive/5"}`}
                  >
                    <div className="font-mono">{cron.name}</div>
                    <div className={cron.ok ? "text-muted-foreground" : "text-destructive"}>
                      {cron.lastSuccessAt
                        ? `${cron.hoursSinceSuccess}h ago (max ${cron.expectedWithinHours}h)`
                        : "never ran"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="User Containers" value={data.containers.length} />
            <StatCard title="Pool Containers" value={data.poolContainers.length} />
            <StatCard
              title="Running"
              value={data.containers.filter((c) => c.deploymentStatus === "running" && !c.idle).length}
              accent="text-emerald-600"
            />
            <StatCard
              title="Idle"
              value={data.containers.filter((c) => c.idle).length}
            />
          </div>

          {(() => {
            return (
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">Warm Pool</h2>
                  <Badge variant="secondary">{data.poolContainers.length}</Badge>
                  {data.poolBelowMinimum && <Badge variant="destructive">Below minimum</Badge>}
                  <div className="ml-auto flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={provisionPool.isPending}
                      onClick={() => provisionPool.mutate()}
                    >
                      <Plus className="size-3.5 mr-1.5" />
                      Add to pool
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {data.warmPool.enabled ? "Claiming from pool" : "Provisioning fresh"}
                    </span>
                    <Switch
                      checked={data.warmPool.enabled}
                      disabled={togglePool.isPending}
                      onCheckedChange={(checked) => togglePool.mutate(checked)}
                      aria-label="Toggle warm pool claiming"
                    />
                  </div>
                </div>
                {data.poolContainers.length > 0 ? (
                  <DataTable
                    columns={poolColumns}
                    data={data.poolContainers}
                    onRowClick={(row) => openContainer(row.id)}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">No containers in the pool.</p>
                )}
              </section>
            );
          })()}

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Containers</h2>
              <Badge variant="secondary">{data.containers.length}</Badge>
              {onVercel > 0 && <span className="text-sm text-muted-foreground">{onVercel} on Vercel Sandbox</span>}
            </div>
            {data.containers.length > 0 ? (
              <DataTable
                columns={containerColumns}
                data={sortContainers(data.containers)}
                onRowClick={(row) => openContainer(row.id)}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No containers.</p>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
